/** @jsx jsx */
import { Interpolation, Theme, jsx } from '@emotion/react'
import {
    AdDefinition,
    CardBreakpointRatios,
    CardItem,
    CollectionEvent,
    createCollectionAvailableEvent,
    FixedRatio,
    Product,
    SizePropsKey,
} from '@news-mono/web-common'
import { MaybeLoaded } from '@west-australian-newspapers/json-react-layouts-data-loader'
import React, { ElementType, Fragment } from 'react'
import { CommonCardProps } from '../../cards/CardItem.Props'
import { MediaMode } from '../../cards/CardMedia/CardMedia'
import { TeaserMode } from '../../cards/CardText/CardTeaser'
import { KickerMode } from '../../cards/Kicker/Kicker'
import { Landscape } from '../../cards/Landscape/Landscape'
import {
    getLandscapeCardItemProps,
    LandscapeLayout,
} from '../../cards/Landscape/Landscape.layouts'
import { LandscapeStacked } from '../../cards/LandscapeStacked/LandscapeStacked'
import { LandscapeWide } from '../../cards/LandscapeWide/LandscapeWide'
import { Portrait } from '../../cards/Portrait/Portrait'
import {
    StyledGridItem,
    StyledHeader,
    StyledJuliet,
} from '../../collections/Juliet/Juliet.styled'
import { TimestampType } from '../../content/CardTimestamp/CardTimestamp'
import {
    ResponsiveContainer,
    ResponsivePictureSizes,
} from '../../content/Picture/responsive'
import {
    SectionHeader,
    SectionHeaderProps,
} from '../../section-header/SectionHeader/SectionHeader'
import { CardSwitcher } from '../../__helpers/CardSwitcher'
import { ImpressionAvailable } from '../../__helpers/impression-available-helper'
import { OverrideThemeSection } from '../../__helpers/override-theme-section'
import { useProduct } from '../../__product/useProduct'
import { metrics, ThemeMargins } from '../../__styling/settings/metrics'
import { Section } from '../../__styling/settings/sections'
import { invertMaybeLoadedItems } from '../helpers/loading'
import { LandscapeNightly } from '../../cards/LandscapeNightly/LandscapeNightly'
import {
    getAdUnitId,
    InterlacedComponent,
    useInterlacedAds,
} from '../../__helpers'

export interface JulietProps extends CommonCardProps, ResponsiveContainer {
    className?: string
    hideByline?: boolean
    hideMedia?: boolean
    sectionHeader?: SectionHeaderProps
    section?: Section
    cardType: LandscapeLayout | 'LandscapeWide'
    cardHasBackground?: boolean
    showLargeCard?: boolean
    fixedRatios?: FixedRatio | FixedRatio[] | CardBreakpointRatios
    imageWidths?: ResponsivePictureSizes
    mediaMode?: MediaMode
    teaserMode?: TeaserMode
    kickerMode?: KickerMode
    removeHorizontalGutters?: boolean
    removeTopListPadding?: boolean
    timestamp?: TimestampType
    verticalSpacing?: keyof ThemeMargins
    onEvent: (event: CollectionEvent) => void
    disableImageLazyLoad?: boolean
    items: MaybeLoaded<CardItem[]>
    stickyOffset?: number
    expectedCards: number
    contentIsTangential?: boolean
    noHorizontalDividers?: boolean
    topicLevel?: 'parent'
    hideLastDivider?: boolean
    numbered?: boolean
    showTeaser?: boolean
    showBasicTeaser?: boolean
    numberOfTeasersToShow?: number
    condensedInfo?: boolean
    allowLiveBlogMilestoneTeaser?: boolean
    interlacedComponents?: Record<'advert', InterlacedComponent>
    interlacedAdSizeDefinition?: SizePropsKey
    itemHeaderAs?: ElementType
}

// Builds an array of the AdUnitDefinitions.
export const getJulietAdUnitDefinitions = (
    cardsBetweenAds: number,
    initialAdOffset: number,
    adSizeDefinition?: SizePropsKey,
) =>
    adSizeDefinition
        ? Array.from(
              { length: cardsBetweenAds },
              (_, i) => initialAdOffset + i * cardsBetweenAds,
          ).map<AdDefinition>((index) => ({
              id: getAdUnitId(index, 0, cardsBetweenAds),
              size: adSizeDefinition,
          }))
        : []

export const Juliet: React.FC<JulietProps> = (props) => {
    const {
        className,
        sectionHeader,
        section,
        onEvent,
        showLargeCard,
        verticalSpacing,
        expectedCards,
        stickyOffset,
        fixedRatios,
        contentIsTangential: contentIstangential,
        noHorizontalDividers,
        hideLastDivider,
        removeTopListPadding,
        showTeaser,
        numberOfTeasersToShow,
        allowLiveBlogMilestoneTeaser,
        interlacedComponents,
        interlacedAdSizeDefinition,
    } = props

    const product = useProduct()
    const items = invertMaybeLoadedItems(props.items, expectedCards)
    const numItems = showLargeCard === true ? items.slice(1) : items

    // Special 2-column layout of Top Story section on The Nightly
    const isTwoColumnOnTablet =
        (interlacedComponents?.advert?.cardsBetweenAdsOnTablets ?? 1) % 2 === 0

    const renderCard = (loadableItem: MaybeLoaded<CardItem>, index: number) => {
        if (
            !loadableItem.loaded ||
            loadableItem.result.cardType === 'marketing-redirect-tile'
        ) {
            return <Fragment></Fragment>
        }

        const firstChildThemeInterpolation: Interpolation<Theme> =
            removeTopListPadding
                ? {
                      '&:first-child': {
                          paddingTop: 0,
                      },
                  }
                : {}
        const lastChildThemeInterpolation: Interpolation<Theme> = {
            '&:last-child': {
                marginBottom: 0,
            },
        }

        const themeInterpolation: Interpolation<Theme> = {
            ...firstChildThemeInterpolation,
            ...lastChildThemeInterpolation,
        }

        if (props.cardType === 'LandscapeWide') {
            return (
                <StyledGridItem
                    key={index}
                    css={themeInterpolation}
                    noHorizontalDividers={noHorizontalDividers === true}
                    hideLastDivider={hideLastDivider}
                >
                    {renderLandscapeWide(
                        loadableItem,
                        index + (showLargeCard ? 2 : 1),
                        props,
                    )}
                </StyledGridItem>
            )
        } else {
            return (
                <StyledGridItem
                    key={index}
                    css={themeInterpolation}
                    noHorizontalDividers={noHorizontalDividers === true}
                    hideLastDivider={hideLastDivider}
                    isTwoColumnOnTablet={isTwoColumnOnTablet}
                >
                    {determineLandscapeCardToRender(
                        loadableItem,
                        index + (showLargeCard ? 2 : 1),
                        props.cardType,
                        props,
                        product,
                        numberOfTeasersToShow,
                        showTeaser,
                    )}
                </StyledGridItem>
            )
        }
    }
    const listResult = useInterlacedAds({
        interlacedComponents,
        items: numItems,
        renderCard,
        enableInterlacedAd: !!interlacedAdSizeDefinition,
    })

    const renderJuliet = () => (
        <ImpressionAvailable
            loading={!props.items.loaded}
            percentageVisibleThreshold={numItems.length >= 10 ? 5 : undefined} // PN sidebar is so long that it never fires an impression, unless we lower the threshold
            available={() => {
                if (!props.items.loaded) {
                    console.warn(
                        'Available should never be called when loading is true',
                    )
                    return
                }
                props.onEvent(
                    createCollectionAvailableEvent(
                        props.items.result,
                        'Juliet',
                        product,
                        props.onEvent,
                    ),
                )
            }}
        >
            {(ref) => (
                <StyledJuliet
                    as={contentIstangential ? 'aside' : 'div'}
                    ref={ref}
                    className={className}
                    verticalSpacing={verticalSpacing}
                    stickyOffset={stickyOffset}
                    isTwoColumnOnTablet={isTwoColumnOnTablet}
                >
                    {sectionHeader && items && (
                        <StyledHeader>
                            <SectionHeader {...sectionHeader} />
                        </StyledHeader>
                    )}
                    {showLargeCard &&
                        items.slice(0, 1).map((item, index) => (
                            <StyledGridItem
                                key={index}
                                style={{
                                    marginBottom: metrics.perthnow.margins.md,
                                }}
                                noHorizontalDividers={
                                    noHorizontalDividers === true
                                }
                                hideLastDivider={hideLastDivider}
                            >
                                <CardSwitcher
                                    onEvent={props.onEvent}
                                    item={item}
                                    cardContext="juliet"
                                    cardNumber={1}
                                    publicationCard={(publicationItem) => (
                                        <Portrait
                                            item={publicationItem}
                                            fixedRatio={fixedRatios || ['16:9']}
                                            onEvent={onEvent}
                                            fontScale={2}
                                            cardNumber={1}
                                            mediaMode={'edgeToEdge'}
                                            hasPadding={true}
                                            teaserMode={
                                                props.teaserMode || 'visible'
                                            }
                                            hideByline={props.hideByline}
                                            kickerMode={props.kickerMode}
                                            containerWidthRatios={
                                                props.containerWidthRatios
                                            }
                                            allowLiveBlogMilestoneTeaser={
                                                allowLiveBlogMilestoneTeaser
                                            }
                                        />
                                    )}
                                />
                            </StyledGridItem>
                        ))}
                    {numItems.length > 0 && listResult}
                </StyledJuliet>
            )}
        </ImpressionAvailable>
    )

    return section ? (
        <OverrideThemeSection section={section}>
            {renderJuliet()}
        </OverrideThemeSection>
    ) : (
        renderJuliet()
    )
}
Juliet.displayName = 'Juliet'

function renderLandscapeWide(
    item: MaybeLoaded<CardItem>,
    cardNumber: number,
    props: JulietProps,
) {
    return (
        <CardSwitcher
            onEvent={props.onEvent}
            item={item}
            cardNumber={cardNumber}
            cardContext="juliet-landscapewide"
            publicationCard={(publicationItem) => (
                <LandscapeWide
                    item={publicationItem}
                    onEvent={props.onEvent}
                    timestamp={props.timestamp}
                    fixedRatio={props.fixedRatios || ['4:3', '16:9']}
                    disableImageLazyLoad={props.disableImageLazyLoad}
                    cardNumber={cardNumber}
                    mediaMode={props.mediaMode}
                    teaserMode={props.teaserMode || 'visible'}
                />
            )}
        />
    )
}

interface RenderLandscapeNightlyProps {
    item: MaybeLoaded<CardItem>
    cardNumber: number
    layout: LandscapeLayout
    props: JulietProps
    numberOfTeasersToShow?: number
    showTeaser?: boolean
}

export function renderLandscapeNightly({
    item,
    cardNumber,
    layout,
    props,
    numberOfTeasersToShow = -1,
    showTeaser = false,
}: RenderLandscapeNightlyProps) {
    const onEvent = props.onEvent
    const renderTeaser = showTeaser && numberOfTeasersToShow >= cardNumber

    return (
        <CardSwitcher
            onEvent={onEvent}
            item={item}
            cardNumber={cardNumber}
            cardContext="juliet-landscapenightly"
            publicationCard={(publicationItem) => (
                <LandscapeNightly
                    {...getLandscapeCardItemProps(
                        publicationItem,
                        layout,
                        { onEvent },
                        cardNumber,
                    )}
                    imageWidths={props.imageWidths}
                    hideByline={props.hideByline}
                    hideMedia={props.hideMedia}
                    topicLevel={props.topicLevel}
                    disableImageLazyLoad={props.disableImageLazyLoad}
                    imagePosition={props.numbered ? 'right' : 'left'}
                    contentPosition={props.numbered ? 'top' : 'center'}
                    displayNumber={props.numbered}
                    renderBasicTeaser={renderTeaser}
                    condensedInfo={props.condensedInfo}
                    headlineAs={props.itemHeaderAs}
                />
            )}
        />
    )
}

export function renderLandscape(
    item: MaybeLoaded<CardItem>,
    cardNumber: number,
    layout: LandscapeLayout,
    props: JulietProps,
) {
    const onEvent = props.onEvent

    return (
        <CardSwitcher
            onEvent={onEvent}
            item={item}
            cardNumber={cardNumber}
            cardContext="juliet-landscape"
            publicationCard={(publicationItem) => (
                <Landscape
                    {...getLandscapeCardItemProps(
                        publicationItem,
                        layout,
                        { onEvent },
                        cardNumber,
                    )}
                    imageWidths={props.imageWidths}
                    hideByline={props.hideByline}
                    fixedRatio={props.fixedRatios || ['4:3', '16:9']}
                    disableImageLazyLoad={props.disableImageLazyLoad}
                />
            )}
        />
    )
}

function renderLandscapeStacked(
    item: MaybeLoaded<CardItem>,
    cardNumber: number,
    props: JulietProps,
) {
    return (
        <CardSwitcher
            onEvent={props.onEvent}
            item={item}
            cardNumber={cardNumber}
            cardContext="juliet-landscapestacked"
            publicationCard={(publicationItem) => (
                <LandscapeStacked
                    item={publicationItem}
                    onEvent={props.onEvent}
                    disableImageLazyLoad={props.disableImageLazyLoad}
                    teaserMode={props.teaserMode || 'visible'}
                    kickerMode={props.kickerMode}
                    hideByline={props.hideByline}
                    fixedRatio={props.fixedRatios || '16:9'}
                    cardNumber={cardNumber}
                    mediaMode={props.mediaMode}
                />
            )}
        />
    )
}

function determineLandscapeCardToRender(
    item: MaybeLoaded<CardItem>,
    cardNumber: number,
    cardType: LandscapeLayout,
    props: JulietProps,
    product: Product,
    numberOfTeasersToShow?: number,
    showTeaser?: boolean,
) {
    if (product === 'sevennews') {
        return renderLandscapeStacked(item, cardNumber, props)
    }

    if (product === 'thenightly') {
        return renderLandscapeNightly({
            item,
            cardNumber,
            layout: cardType,
            props,
            numberOfTeasersToShow,
            showTeaser: showTeaser ?? false,
        })
    }
    return renderLandscape(item, cardNumber, cardType, props)
}
